import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import MainLayout from '../../components/layout/MainLayout';
import {
  assignDealerAdmin,
  assignDealerTenant,
  disableDealerTenant,
  getDealer,
  getTenantsAdminList,
  getUsers,
  listAllDealerAssignments,
  listDealerAdmins,
  listUserDirectory,
  listDealerAssignments,
  updateDealer,
  updateDealerAdmin,
  updateDealerAdminAlgo,
} from '../../api/backendApi';
import { ALGO_DEVICE_SUPPORT_HELP, ALGO_DEVICE_SUPPORT_LABEL } from './algoDeviceSupport';
import { ROUTES } from '../../utils/constants';
import { availableTenants, dealerAdminOption, rowsFrom, safeDealerError, userChoiceLabel } from './dealerAccess';

export default function DealerDetailPage() {
  const { dealerId } = useParams();
  const [dealer, setDealer] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [tenantChoices, setTenantChoices] = useState([]);
  const [userChoices, setUserChoices] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [userId, setUserId] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    const [dealerResponse, adminResponse, assignmentResponse, allAssignments, tenants, directory, users] = await Promise.all([
      getDealer(dealerId),
      listDealerAdmins(dealerId),
      listDealerAssignments(dealerId),
      listAllDealerAssignments(),
      getTenantsAdminList(),
      listUserDirectory().catch(() => null),
      getUsers(),
    ]);
    setDealer(dealerResponse.data);
    setAdmins(rowsFrom(adminResponse));
    setAssigned(rowsFrom(assignmentResponse));
    setTenantChoices(availableTenants(tenants, rowsFrom(allAssignments), dealerId));
    const directoryRows = rowsFrom(directory);
    const people = directoryRows.length ? directoryRows : (users || []);
    setUserChoices(people.map((user) => dealerAdminOption(user)).filter((user) => user.id));
  };

  useEffect(() => {
    load().catch((err) => setError(safeDealerError(err)));
  }, [dealerId]);

  const save = async (patch) => {
    setError('');
    try {
      await updateDealer(dealerId, patch);
      await load();
    } catch (err) {
      setError(safeDealerError(err));
    }
  };

  const assignTenant = async () => {
    if (!tenantId) return;
    setError('');
    try {
      await assignDealerTenant(dealerId, tenantId);
      setTenantId('');
      await load();
    } catch (err) {
      setError(safeDealerError(err));
    }
  };

  const disableTenant = async (id, name) => {
    if (!window.confirm(`Disable the assignment for ${name || 'this tenant'}? The tenant, its users, and its Sites stay unchanged.`)) return;
    setError('');
    try {
      await disableDealerTenant(dealerId, id);
      await load();
    } catch (err) {
      setError(safeDealerError(err));
    }
  };

  const assignAdmin = async () => {
    if (!userId) return;
    setError('');
    try {
      await assignDealerAdmin(dealerId, userId);
      setUserId('');
      await load();
    } catch (err) {
      setError(safeDealerError(err));
    }
  };

  const changeAlgo = async (adminId, tenant, enabled) => {
    setError('');
    try {
      await updateDealerAdminAlgo(dealerId, adminId, tenant, enabled);
      await load();
    } catch (err) {
      setError(safeDealerError(err));
    }
  };

  const changeAdmin = async (id, status) => {
    const verb = status === 'revoked' ? 'Revoke' : 'Disable';
    if (!window.confirm(`${verb} this Dealer Admin membership? This does not change their platform role or tenant memberships.`)) return;
    setError('');
    try {
      await updateDealerAdmin(dealerId, id, status);
      await load();
    } catch (err) {
      setError(safeDealerError(err));
    }
  };

  return (
    <MainLayout scrollMain>
      <Box sx={{ p: 3, maxWidth: 880 }}>
        <Button component={Link} to={ROUTES.ADMIN_DEALERS} sx={{ mb: 2 }}>Back to Dealers</Button>
        <Typography variant="h4" sx={{ mb: 1 }}>{dealer?.name || 'Dealer'}</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Dealer access is limited to assigned tenants. Assignment does not change tenant ownership, Sites, billing, or markup.
        </Alert>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {dealer ? (
          <Box sx={{ display: 'grid', gap: 2, maxWidth: 480, mb: 4 }}>
            <TextField label="Name" value={dealer.name || ''} onChange={(event) => setDealer({ ...dealer, name: event.target.value })} />
            <TextField label="Contact name" value={dealer.contactName || ''} onChange={(event) => setDealer({ ...dealer, contactName: event.target.value })} />
            <TextField label="Contact email" value={dealer.contactEmail || ''} onChange={(event) => setDealer({ ...dealer, contactEmail: event.target.value })} />
            <TextField select label="Status" value={dealer.status || 'active'} onChange={(event) => setDealer({ ...dealer, status: event.target.value })}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </TextField>
            <Button variant="contained" onClick={() => {
              const patch = { name: dealer.name, status: dealer.status };
              if (dealer.contactName) patch.contactName = dealer.contactName;
              if (dealer.contactEmail) patch.contactEmail = dealer.contactEmail;
              save(patch);
            }}
            >
              Save dealer
            </Button>
          </Box>
        ) : null}

        <Typography variant="h5" sx={{ mb: 1 }}>Assigned tenants</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, maxWidth: 560 }}>
          <TextField select fullWidth label="Available tenant" value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
            {tenantChoices.map((tenant) => (
              <MenuItem key={tenant.id || tenant.tenantId} value={tenant.id || tenant.tenantId}>
                {tenant.name || tenant.id || tenant.tenantId}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" onClick={assignTenant} disabled={!tenantId}>Assign</Button>
        </Box>
        {assigned.filter((row) => row.status === 'active').map((row) => (
          <Box key={row.tenantId} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
            <Typography>{row.tenantName || row.tenantId}</Typography>
            <Button color="warning" onClick={() => disableTenant(row.tenantId, row.tenantName)}>Disable assignment</Button>
          </Box>
        ))}

        <Typography variant="h5" sx={{ mt: 4, mb: 1 }}>Dealer Admins</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, maxWidth: 560 }}>
          <TextField select fullWidth label="Existing user" value={userId} onChange={(event) => setUserId(event.target.value)}>
            {userChoices.map((user) => (
              <MenuItem key={user.id} value={user.id} disabled={!user.selectable}>
                {user.selectable ? `${user.label} · ${user.email}` : `${user.label} · no Auth account`}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" onClick={assignAdmin} disabled={!userId || userChoices.some((user) => user.id === userId && !user.selectable)}>Assign</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{ALGO_DEVICE_SUPPORT_HELP}</Typography>
        {admins.map((admin) => (
          <Box key={admin.userId} sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography>
              {userChoices.find((user) => user.id === admin.userId)?.label || 'Dealer Admin'}
              {admin.userId ? ` · ${admin.userId}` : ''}
              {' · '}{admin.role} · {admin.status}
              {!userChoices.find((user) => user.id === admin.userId)?.selectable && admin.status === 'active' ? ' · orphaned access' : ''}
            </Typography>
            {admin.status === 'active' ? (
              <Box>
                <Button onClick={() => changeAdmin(admin.userId, 'disabled')}>Disable</Button>
                <Button color="warning" onClick={() => changeAdmin(admin.userId, 'revoked')}>Revoke</Button>
              </Box>
            ) : null}
          </Box>
          {admin.status === 'active' ? assigned.filter((row) => row.status === 'active').map((row) => (
            <FormControlLabel
              key={`${admin.userId}-${row.tenantId}`}
              control={(
                <Checkbox
                  checked={admin.algoDeviceSupportByTenant?.[row.tenantId] === true}
                  onChange={(event) => changeAlgo(admin.userId, row.tenantId, event.target.checked)}
                />
              )}
              label={`${ALGO_DEVICE_SUPPORT_LABEL} · ${row.tenantName || row.tenantId}`}
            />
          )) : null}
          </Box>
        ))}
      </Box>
    </MainLayout>
  );
}
